
import { applyLogic } from '@joyfill/conditional-logic';

import { swapId } from './utils';
import { resolveColumnRequired, resolveSchemaTableRequired } from './requiredLogic';
import { resolveColumnCellHidden } from './cellVisibilityLogic';
import { isFieldHiddenInView } from './documentHelper';
import { getResolvedCellsByIndex } from './tableRowsHelper';

/**
 * Local empty-cell check (kept here to avoid a circular import with validation.js).
 * Mirrors validation.isEmptyCellValue.
 *
 * @param {*} value
 * @param {String} columnType
 * @returns {Boolean}
 */
const isCellEmpty = (value, columnType) => {
  if (columnType === 'image' || columnType === 'multiSelect') {
    return !Array.isArray(value) || value.length === 0 || value.every((item) => item === '');
  }
  return value === '' || value === null || value === undefined;
};
import FieldDisplayTypes from '../constants/FieldDisplayTypes';
import FieldTypes from '../constants/FieldTypes';
import FieldModes from '../constants/FieldModes';

/**
 * <--------------NOTICE----------------->
 * The functions mentioned below are used in the collection field as well
 * as the field validations. If you are making any changes to the functions
 * please make sure to check both the places this has been used.
 */

/**
 *
 * This helper method applies logic to determine if a child schema should be shown or hidden. Should
 * return true if the child is visible or false if child should be hidden.
 *
 * @param {Object} schemaEntry
 * @param {Object} rowCells
 * @param {String} fieldMode
 * @returns Boolean
 */
export const customApplyLogic = (schemaEntry, rowCells = {}, fieldMode, docFields = []) => {

  if (!schemaEntry) return false;

  // Always show in edit mode
  if (fieldMode === FieldModes.edit) return true;

  //Check if schema has logic. If schema has logic then continue,
  //or else return true if hidden is not set or false if hidden is set to true.
  if (!schemaEntry.logic) return typeof schemaEntry.hidden === 'boolean' ? !schemaEntry.hidden : true;

  const hasLogic = (schemaEntry.logic && schemaEntry.logic.action && schemaEntry.logic.eval && schemaEntry.logic.conditions && schemaEntry.logic.conditions.length > 0);

  if (!hasLogic) return typeof schemaEntry.hidden === 'boolean' ? !schemaEntry.hidden : true;

  const logic = schemaEntry.logic;

  const { action, eval: evalType, conditions } = logic;

  const evaluations = conditions.map((conditionEntry) => {

    const { column, field, condition, value } = conditionEntry;

    let cellValue;

    if (field) {
      const targetField = docFields.find(f => f._id === field);
      if (!targetField) return false;
      cellValue = targetField.value;
    } else {
      cellValue = rowCells[column];
    }

    switch (condition) {
      case '=': // equal
        return (
          (!Array.isArray(cellValue) && new RegExp(`^${value}$`, 'i').test(cellValue)) ||
          (Array.isArray(cellValue) && cellValue.includes(value))
        );

      case '!=': // notEqual
        return (
          (!Array.isArray(cellValue) && !(new RegExp(`^${value}$`, 'i').test(cellValue))) ||
          (Array.isArray(cellValue) && !cellValue.includes(value))
        );

      case '*=': // filled
        return (
          (cellValue && !Array.isArray(cellValue)) ||
          (Array.isArray(cellValue) && cellValue.length > 0)
        );

      case 'null=': // empty
        return (
          cellValue === '' ||
          cellValue === undefined ||
          cellValue === null ||
          (Array.isArray(cellValue) && cellValue.length < 1)
        );

      case '?=': // contain
        return (
          typeof cellValue === 'string' &&
          new RegExp(value, 'gi').test(cellValue)
        );

      case '>': // greaterThan
        return cellValue > value;

      case '<': // lessThan
        return cellValue < value;

      default:
        return false;
    }

  });

  const passed = evalType === 'and'
    ? evaluations.every(Boolean)
    : evaluations.some(Boolean);

  if (action === 'show') {
    return passed || (typeof schemaEntry.hidden === 'boolean' ? !schemaEntry.hidden : true);
  } else {
    return !passed && (typeof schemaEntry.hidden === 'boolean' ? !schemaEntry.hidden : true);
  }

};

const LOGIC_KEYS = ['logic', 'requiredLogic', 'cellRequiredLogic', 'cellVisibilityLogic'];

// Rendering shares this cache across immutable updates. Public validation supplies a fresh
// cache per call, so mutable documents cannot reuse stale segments, columns or cell values.
export const createCollectionCache = () => ({
  schemaDocFieldReferences: new WeakMap(),
  visibleColumns: new WeakMap(),
  resolvedCells: new WeakMap(),
  flattenContexts: [],
});

const renderCollectionCache = createCollectionCache();

/**
 * Whether any schema or column condition reads a document field. Only then can a change to
 * docFields change the flattened rows.
 *
 * @param {Object} schema
 * @returns {Boolean}
 */
const doesSchemaReferenceDocFields = (schema, cache) => {

  const cached = cache.schemaDocFieldReferences.get(schema);
  if (cached !== undefined) return cached;

  const holderReferencesField = (holder) => holder && LOGIC_KEYS.some((key) => (
    Array.isArray(holder[key]?.conditions) && holder[key].conditions.some((condition) => condition && condition.field)
  ));

  const references = Object.keys(schema).some((schemaKey) => {
    const schemaEntry = schema[schemaKey];
    return holderReferencesField(schemaEntry) || (schemaEntry?.tableColumns || []).some(holderReferencesField);
  });

  cache.schemaDocFieldReferences.set(schema, references);
  return references;

};

/**
 * Flattened row segments (a row followed by everything nested under it), cached per flatten
 * context and per row object. The collection change helpers only clone the path to the row they
 * change, so every other row keeps its object and its segment is reused as is. That keeps an edit
 * in a 30k row collection from rebuilding 30k items, and keeps the item objects of unchanged rows
 * stable for the row components.
 */
const FLATTEN_CONTEXT_LIMIT = 4;
const getFlattenSegmentCache = (context, cache) => {

  const { flattenContexts } = cache;
  const matches = (candidate) => Object.keys(context).every((key) => candidate.context[key] === context[key]);

  let entry = flattenContexts.find(matches);

  if (!entry) {
    entry = { context, segments: new WeakMap() };
    flattenContexts.unshift(entry);
    if (flattenContexts.length > FLATTEN_CONTEXT_LIMIT) flattenContexts.pop();
  }

  return entry.segments;

};

export const flattenArray = (type, displayType, fieldPositionSchema, schema, value, disabled, fieldMode, view, docFields, cache = renderCollectionCache) => {

  const segmentCache = getFlattenSegmentCache({
    type,
    displayType,
    fieldPositionSchema,
    schema,
    disabled,
    fieldMode,
    view,
    docFields: doesSchemaReferenceDocFields(schema, cache) ? docFields : null,
  }, cache);

  const flattenedArray = [
    {
      type: 'fieldTitle',
      layoutType: 'title',
      layoutKey: 'title',
    }
  ];

  function traverseNodes(depth, node, parentId = null, schemaKey = null, nestedPath = '', rowIndex, calculatedCells = {}) {

    const isRoot = schema[schemaKey]?.root;

    if (!node || typeof node !== 'object') return;

    /**
     * Handle the value (rows) array
     */
    if (Array.isArray(node)) {

      /**
       * NO-2347 column formulas. The sibling rows of one schema table, exactly as they will
       * render and validate. Deleted rows are dropped here once, so formula resolution (A1
       * numbering), rendering, validation and the header / footer decision cannot disagree.
       * Results are read by sibling position, never by row id, so two siblings that share an
       * id keep their own calculated cells.
       */
      const siblingRows = node.filter((item) => item && !item.deleted);
      const resolvedRows = getResolvedCellsByIndex(siblingRows, schema[schemaKey]?.tableColumns || [], cache.resolvedCells);

      const shouldChildrenDisplayHeader = siblingRows.length > 0 || isRoot;
      const shouldChildrenDisplayFooter = (siblingRows.length > 0 || isRoot) && !disabled && displayType !== FieldDisplayTypes.rte;

      if (shouldChildrenDisplayHeader && displayType === FieldDisplayTypes.original) {
        flattenedArray.push(
          {
            _id: getStructuralItemId(parentId, schemaKey, 'title'),
            type: 'title',
            schemaKey,
            schema: schema[schemaKey],
            fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
            parentId,
            depth,
            nestedPath,

            /**
             * layoutType and layoutKey are used to handle 
             * PDF Rendering, Page Breaks and Measurement 
             * management
             */
            layoutType: 'title', //Layout type should be header because header has special functioinality in PDF Rendering
            layoutKey: parentId ? `${parentId}-${schemaKey}-title` : `${schemaKey}-title`,
          },
        );

      }

      if (shouldChildrenDisplayHeader && type === FieldTypes.collection) {
        flattenedArray.push({
          _id: getStructuralItemId(parentId, schemaKey, 'header'),
          type: 'header',
          schemaKey,
          schema: schema[schemaKey],
          fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
          parentId,
          depth,
          nestedPath,

          /**
           * layoutType and layoutKey are used to handle 
           * PDF Rendering page preaks and measurements.
           */
          layoutType: 'header',
          layoutKey: parentId ? `${parentId}-${schemaKey}-header` : `${schemaKey}-header`,
        });
      }

      // Update nestedPath for each item (using the index for uniqueness: it addresses the real array)
      let siblingIndex = 0;
      node.forEach((item, index) => {
        if (!item || item.deleted) return; //Same rule as siblingRows
        const itemPath = nestedPath ? `${nestedPath}.${index}` : `${index}`;
        const itemCalculatedCells = resolvedRows[siblingIndex] || {};
        siblingIndex += 1;
        traverseNodes(depth, item, parentId, schemaKey, itemPath, index, itemCalculatedCells);
      });

      if (shouldChildrenDisplayFooter) {
        /**
         * Footer does not contain layoutType or layoutKey because
         * it is not rendering duering PDF rendering.
         */
        flattenedArray.push(
          {
            _id: getStructuralItemId(parentId, schemaKey, 'footer'),
            type: 'footer',
            schemaKey,
            schema: schema[schemaKey],
            fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
            parentId,
            depth,
            nestedPath
          },
        );
      }
    }

    /**
     * Handle the row obect and row children
     */
    else {

      const cachedSegment = segmentCache.get(node);

      if (cachedSegment
        && cachedSegment.parentId === parentId
        && cachedSegment.schemaKey === schemaKey
        && cachedSegment.nestedPath === nestedPath
        && cachedSegment.rowIndex === rowIndex
        && cachedSegment.depth === depth
        && cachedSegment.calculatedCells === calculatedCells) {
        for (let i = 0; i < cachedSegment.items.length; i++) flattenedArray.push(cachedSegment.items[i]);
        return;
      }

      const segmentStart = flattenedArray.length;

      const { _id, cells, children, tz, decorators } = node;
      // Create a current path for this row.
      const currentPath = nestedPath ? `${nestedPath}` : _id;

      //We don't show the invalid indicator for PDF mode. The node is the row currentPath points at.
      const isInvalid = detectEmptyChildren(fieldPositionSchema, schema, schemaKey, node, fieldMode, view, docFields, calculatedCells, cache);

      flattenedArray.push({
        _id,
        type: 'row',
        parentId,
        schemaKey,
        schema: schema[schemaKey],
        fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
        cells: cells || {},
        calculatedCells, //NO-2347: display values with column formulas resolved (text columns)
        rowIndex,
        depth,
        nestedPath: currentPath,
        isInvalid,
        tz,
        decorators,

        /**
         * layoutType and layoutKey are used to handle 
         * PDF Rendering page breaks and measurements.
         */
        header: parentId ? `${parentId}-${schemaKey}-header` : `${schemaKey}-header`,
        layoutType: 'row',
        layoutKey: parentId ? `${parentId}-${schemaKey}-${_id}` : `${schemaKey}-${_id}`,
      });

      if (children) {

        const defaultChildSchemaKeys = schema[schemaKey]?.children || [];
        const rowChildrenSchemaKeys = children ? Object.keys(children) : [];
        const mergedSchemaKeys = defaultChildSchemaKeys.concat(rowChildrenSchemaKeys);
        const nestedSchemaKeys = mergedSchemaKeys.filter((item, index) => mergedSchemaKeys.indexOf(item) === index); //Remove duplicates

        nestedSchemaKeys.forEach((childSchemaKey) => {

          const childSchema = schema[childSchemaKey];
          const childData = children?.[childSchemaKey];
          const childPath = `${currentPath}.children.${childSchemaKey}.value`; // Append the child schema key to the current path
          const isVisible = customApplyLogic(childSchema, cells, fieldMode, docFields);

          if (childData && isVisible) {
            traverseNodes(depth + 1, childData.value, _id, childSchemaKey, childPath, undefined, cells);
          }
        });
      }

      segmentCache.set(node, { parentId, schemaKey, nestedPath, rowIndex, depth, calculatedCells, items: flattenedArray.slice(segmentStart) });
    }
  }

  const rootSchemaKey = Object.keys(schema).find((key) => schema[key].root);
  traverseNodes(0, value, null, rootSchemaKey);

  return flattenedArray;

};

/**
 * Retruns the value corresponding to the nested path.
 *
 * @param {array} data
 * @param {string} path
 * @returns{object}
 */
export const getNestedItem = (data, path) => {

  // Normalize the path into an array; convert numeric strings to numbers.
  const keys = Array.isArray(path)
    ? path
    : path.split('.').map((k) => (isNaN(k) ? k : Number(k)));

  // If path is "0" or not provided, we return the data itself.
  if (!path) {
    return data;
  }

  // Traverse the data structure following the keys.
  return keys.reduce((acc, key) => {
    if (acc === undefined || acc === null) {
      return undefined;
    }
    return acc[key];
  }, data);

};

/**
 * Stable id for the title / header / footer items of one schema table. They used to get a fresh
 * ObjectId on every flatten (tens of thousands per render for a large collection) although
 * nothing reads them. Unique per schema table, like their layoutKey.
 */
const getStructuralItemId = (parentId, schemaKey, type) => (parentId ? `${parentId}-${schemaKey}-${type}` : `${schemaKey}-${type}`);

/**
 * tableColumns -> last { view, docFields, visibleColumns }. Column conditional logic only depends
 * on the columns and the doc fields, so it is evaluated once per schema instead of once per row.
 */
const getSchemaVisibleColumns = (tableColumns, view, docFields, cache) => {

  const cacheable = Array.isArray(tableColumns);
  const cached = cacheable ? cache.visibleColumns.get(tableColumns) : null;
  if (cached && cached.view === view && cached.docFields === docFields) return cached.visibleColumns;

  const evaluated = docFields ? applyLogic(tableColumns, docFields, '_id') : tableColumns;
  const visibleColumns = (evaluated || []).filter((column) => !column.deleted && !column.hidden && !isFieldHiddenInView(column.hiddenViews, view));

  if (cacheable) cache.visibleColumns.set(tableColumns, { view, docFields, visibleColumns });
  return visibleColumns;

};

export const detectEmptyChildren = (fieldPositionSchema, schemaObj, schemaKey, nestedValue, fieldMode, view, docFields, calculatedCells = {}, cache = renderCollectionCache) => {

  let emptyChildren = false;

  const rootSchema = schemaObj[schemaKey];

  if (fieldMode === FieldModes.readonly) return;

  //Logic that checks for nested schema validation
  rootSchema.children.forEach((childSchemaId) => {

    const childSchema = schemaObj[childSchemaId];

    if (!childSchema) return;

    const isChildVisible = customApplyLogic(childSchema, nestedValue.cells, undefined, docFields);

    if (resolveSchemaTableRequired(childSchema, nestedValue.cells, docFields) && isChildVisible) {

      if (!nestedValue.children[childSchemaId] || !nestedValue.children[childSchemaId].value || nestedValue.children[childSchemaId].value.length === 0) {
        emptyChildren = true;
      }

    }

  });

  const visibleColumns = getSchemaVisibleColumns(rootSchema.tableColumns, view, docFields, cache);

  const cells = nestedValue.cells || {};

  visibleColumns.forEach((column) => {
    //NO-2347: a text column is judged on its resolved value (a column formula result counts as filled, Error is non-empty)
    const valueToJudge = column.type === 'text' && column._id in calculatedCells ? calculatedCells[column._id] : cells[column._id];
    if (!resolveColumnCellHidden(column, cells, docFields)
      && resolveColumnRequired(column, cells, docFields)
      && isCellEmpty(valueToJudge, column.type)) {
      emptyChildren = true;
    }
  });

  return emptyChildren;

};

/**
 * This function updates the schema and any children references to the schemawith readable 
 * IDs based on the provided readableIdMap lookup.
 * 
 * @param {*} schema 
 * @param {*} readableIdMap { oldCollectionId: newReadableId}
 * @returns {Object} - The updated schema and children references with new readable IDs.
 */
export const updateSchemaWithReadableIds = (schema, readableIdMap) => {

  const updatedSchema = {};

  // Step 1: Rename top-level keys
  for (const oldKey in schema) {

    const newKey = readableIdMap[oldKey] || oldKey;
    const individualSchema = { ...schema[oldKey] };

    // Step 2: Replace child IDs if needed
    if (Array.isArray(individualSchema.children)) {
      individualSchema.children = individualSchema.children.map(childId =>
        readableIdMap[childId] || childId
      );
    }

    // Step 2: Replace logic schema IDs if needed (only for column-type conditions)
    if (individualSchema.logic && individualSchema.logic.conditions) {
      individualSchema.logic.conditions = individualSchema.logic.conditions.map(condition => {
        if (!condition.schema) return condition;
        return { ...condition, schema: readableIdMap[condition.schema] || condition.schema };
      });
    }

    updatedSchema[newKey] = individualSchema;

  }

  return updatedSchema;

}

/**
 * Updates the field value data structure by replacing schema keys with their corresponding readable IDs.
 * 
 * This function is used when a schema's _id is changed to a new readable ID, and all references to the old schemaKey
 * in the nested field value structure (including children) need to be updated to the new key.
 * 
 * @param {Array|Object} fieldValue - The original field value (rows) to update.
 * @param {Array} flattenedRows - An array of objects representing the flattened structure of the field value,
 *                                each containing a `nestedPath` (dot-separated path to the schemaKey) and `schemaKey`.
 * @param {Object} readableIdMap - A mapping of old schema keys to new readable IDs (e.g., { oldId: newId }).
 * @returns {Array|Object} - The updated field value with schema keys replaced by their new readable IDs.
 */
export function updateFieldValueWithReadableIds(fieldValue, flattenedRows, readableIdMap) {
  // Helper function to deep clone the fieldValue to avoid mutating the original data
  const cloneDeep = (obj) => JSON.parse(JSON.stringify(obj));

  // Create a deep copy of the fieldValue to work on
  const updatedRows = cloneDeep(fieldValue);

  // Iterate over each flattened row, which contains the path and schemaKey to potentially update
  for (const row of flattenedRows) {
    const { nestedPath, schemaKey } = row;
    // Find the new key for this schemaKey, if it exists in the readableIdMap
    const newKey = readableIdMap[schemaKey];
    // If there is no new key or the key hasn't changed, skip this row
    if (!newKey || newKey === schemaKey) continue;

    // Split the nestedPath into its parts for traversal
    const parts = nestedPath.split('.');

    // Start traversing from the root of the updatedRows structure
    let ref = updatedRows;

    // Traverse the path up to the parent of the key to be renamed
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      const nextKey = parts[i + 1];

      // If the current reference is an array, use numeric index
      if (Array.isArray(ref)) {
        ref = ref[Number(key)];
      } else {
        ref = ref[key];
      }

      // If the reference is undefined or null, break out of the loop
      if (!ref) break;

      // Check if we're at the parent object whose child key needs to be renamed
      // Specifically, if the current key is "children" and the next key is the schemaKey to rename
      if (
        key === "children" &&
        nextKey === schemaKey &&
        typeof ref === "object" &&
        ref.hasOwnProperty(schemaKey)
      ) {
        // Rename the key in place: assign the value to the new key and delete the old key
        ref[newKey] = ref[schemaKey];
        delete ref[schemaKey];
        // Once renamed, break out of the traversal for this row
        break;
      }
    }
  }

  // Return the updated field value structure with schema keys replaced
  return updatedRows;
}

/**
 * For Document fieldPositions that have multiple table cell fieldPositions associated
 * with the same table field, we need to keep the column property in sync.
 * 
 * @param {*} nextDoc 
 * @param {*} fileId 
 * @param {*} fieldId 
 * @param {*} columnChanges //Column change data: { schemaId, oldColumnId, newColumnId }
 */
export const syncCollectionFieldColumnIdChange = (nextDoc, fileId, fieldId, fieldIndex, columnChanges) => {

  const { schemaId, oldColumnId, newColumnId } = columnChanges;

  const fileIndex = nextDoc.files.findIndex(f => f._id === fileId);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * Step 1: Handle fieldPosition updates 
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */
  const updateFieldPositionColumnId = (fp) => {
    if (fp.field !== fieldId) return fp;

    const nextFp = { ...fp };

    //Update the tableColumns property to the newColumnId if it matches the oldColumnId
    if (nextFp.schema && nextFp.schema?.[schemaId] && nextFp.schema?.[schemaId]?.tableColumns && nextFp.schema?.[schemaId]?.tableColumns[oldColumnId]) {
      nextFp.schema = {
        ...nextFp.schema,
        [schemaId]: {
          ...nextFp.schema[schemaId],
          tableColumns: swapId(nextFp.schema[schemaId].tableColumns, oldColumnId, newColumnId)
        }
      };
    }

    return nextFp;

  }

  // Update primary view fieldPositions
  nextDoc.files[fileIndex].pages = nextDoc.files[fileIndex].pages.map(page => ({
    ...page,
    fieldPositions: page.fieldPositions.map(updateFieldPositionColumnId)
  }));

  // Update mobile view fieldPositions if they exist
  if (nextDoc.files[fileIndex].views && nextDoc.files[fileIndex].views.length > 0) {
    nextDoc.files[fileIndex].views = nextDoc.files[fileIndex].views.map(view => {
      if (view.pages) {
        return {
          ...view,
          pages: view.pages.map(page => ({
            ...page,
            fieldPositions: page?.fieldPositions.map(updateFieldPositionColumnId)
          }))
        };
      }
      return view;
    });
  }

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * Step 1: Handle field updates 
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  //Handle updating the schema conditional logic references to the newColumnId if it matches the oldColumnId
  nextDoc.fields[fieldIndex].schema = swapColumnIdInLogicConditions(nextDoc.fields[fieldIndex].schema, schemaId, oldColumnId, newColumnId);

  return nextDoc;

}

/**
 * Swaps all occurrences of a column id with a new column id in the logic.conditions array
 * for all schema objects in the given schemaObj.
 *
 * @param {Object} schemaObj - The schema object, keyed by schemaId.
 * @param {string} oldColumnId - The column id to replace.
 * @param {string} newColumnId - The new column id to use.
 * @returns {Object} - A new schema object with column ids swapped in logic.conditions.
 */
export function swapColumnIdInLogicConditions(schemaObj, targetSchemaId, oldColumnId, newColumnId) {
  if (!schemaObj || typeof schemaObj !== 'object') return schemaObj;

  // Deep clone to avoid mutation
  const nextSchemaObj = JSON.parse(JSON.stringify(schemaObj));

  /**
   * Conditional Logic (`logic`) and Conditional Required Logic (`requiredLogic`,
   * `cellRequiredLogic`) share the same condition shape, so a column rename must be
   * swapped across every logic-like key.
   *
   * IMPORTANT NOTE: Only update the condition if the schemaId matches the targetSchemaId
   * and the columnId matches the oldColumnId. Different schemas could have the same columnId,
   * this ensures only the condition for the targetSchemaId is updated.
   */
  const logicKeys = ['logic', 'requiredLogic', 'cellRequiredLogic', 'cellVisibilityLogic'];

  const swapColumnInHolder = (holder) => {
    if (!holder || typeof holder !== 'object') return;
    logicKeys.forEach((key) => {
      const logicObj = holder[key];
      if (logicObj && Array.isArray(logicObj.conditions)) {
        logicObj.conditions = logicObj.conditions.map((condition) => {
          if (condition && condition.schema === targetSchemaId && condition.column === oldColumnId) {
            return { ...condition, column: newColumnId };
          }
          return condition;
        });
      }
    });
  };

  Object.keys(nextSchemaObj).forEach((schemaId) => {
    const schema = nextSchemaObj[schemaId];
    if (!schema || typeof schema !== 'object') return;

    // Schema-level logic keys
    swapColumnInHolder(schema);

    // Column-level logic keys
    if (Array.isArray(schema.tableColumns)) {
      schema.tableColumns.forEach((column) => swapColumnInHolder(column));
    }
  });

  return nextSchemaObj;
}

export function getSchemaRows(value, schema, targetSchemaId) {
  if (!value || !schema) return [];

  const rootSchemaKey = Object.keys(schema).find((key) => schema[key].root);

  if (targetSchemaId === rootSchemaKey) {
    return (value || []).filter((r) => !r.deleted);
  }

  const rows = [];

  function traverse(currentValue, currentSchemaKey) {
    if (!currentValue || !Array.isArray(currentValue)) return;

    for (const row of currentValue) {
      if (row.deleted) continue;
      if (!row.children) continue;

      const childSchemaKeys = schema[currentSchemaKey]?.children || [];
      for (const childKey of childSchemaKeys) {
        if (childKey === targetSchemaId) {
          const childRows = row.children?.[childKey]?.value || [];
          rows.push(...childRows.filter((r) => !r.deleted));
        } else {
          traverse(row.children?.[childKey]?.value, childKey);
        }
      }
    }
  }

  traverse(value, rootSchemaKey);
  return rows;
}
